/**
 * The team block library on the editor side: which blocks "Save as block" captures, how they become
 * a block's contentJson (a one-section CanonicalDocument whose body is the block), and how a library
 * block comes back into the editor.
 *
 * Saving converts with the same docToCdm the editor's save uses, inside a section carrying the
 * source section's page setup (so the block previews at the right width) but no header/footer, and
 * keeps only the footnotes/endnotes/comments the blocks reference. The document properties go with
 * it as the editor's save sends them.
 *
 * Inserting brings those notes/comments back: they are renumbered past the open document's ids
 * (remapBlockNotes), the inserted references rewritten to match, and handed to the page's NoteSink so
 * they join its document meta and save. Undoing the insert removes the references but not the meta
 * entries, so the page drops notes that came from an insert and are no longer referenced when it
 * builds the document (pruneInsertedNotes). Notes that were already in the document are never pruned:
 * the editor's save has always kept an unreferenced note, and still does.
 */
import type { Editor, JSONContent } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'
import { docToCdm } from '../cdm/toCdm'
import { blocksToPm, type EditorDocument } from '../cdm/fromCdm'
import type { MovableBlock } from '../cdm/blockMove'
import type { CanonicalDocument, Comment as CdmComment, DocSection, Endnote, Footnote } from '../cdm/types'
import { blocksApiService, type ContentBlockSummary } from '../services/blocksApiService'
import { collectImageBlobRefs, resolveImageSources } from '../cdm/imageSources'
import { templatesApiService } from '../services/templatesApiService'
import { insertBlocks } from './paletteInsert'

export const TEAM_BLOCK_CATEGORIES = ['Layout', 'Branding', 'Parties', 'Commerce', 'Legal', 'Logic', 'Media']

/** The blocks to save: consecutive siblings `nodes` spanning `from`–`to`, inside `section`. */
export interface BlockRange {
  from: number
  to: number
  nodes: PMNode[]
  section: PMNode
}

/**
 * The top-level blocks a non-empty selection touches — a text selection expands to the whole
 * blocks it starts and ends in. A selection across sections is not one block (null).
 */
export function selectionBlockRange(state: EditorState): BlockRange | null {
  const { selection, doc } = state
  if (selection.empty) return null
  const { $from, $to } = selection
  const sectionIdx = $from.index(0)
  const toSectionIdx = $to.depth === 0 ? Math.max(sectionIdx, $to.index(0) - 1) : $to.index(0)
  if (toSectionIdx !== sectionIdx) return null
  const section = doc.child(sectionIdx)
  if (section.type.name !== 'section' || !section.childCount) return null
  const sectionStart = $from.depth === 0 ? $from.pos + 1 : $from.start(1)

  const first = $from.depth === 0 ? 0 : Math.min($from.index(1), section.childCount - 1)
  let last: number
  if ($to.depth === 0) last = section.childCount - 1
  else if ($to.depth === 1) last = $to.index(1) - 1
  else last = $to.index(1)
  last = Math.max(first, Math.min(last, section.childCount - 1))

  const nodes: PMNode[] = []
  let from = sectionStart
  for (let i = 0; i < first; i++) from += section.child(i).nodeSize
  let to = from
  for (let i = first; i <= last; i++) { nodes.push(section.child(i)); to += section.child(i).nodeSize }
  return { from, to, nodes, section }
}

/** The range for one block the drag handle points at (it may sit inside a loop or branch). */
export function movableBlockRange(doc: PMNode, block: MovableBlock): BlockRange {
  const $pos = doc.resolve(block.pos)
  return { from: block.pos, to: block.pos + block.node.nodeSize, nodes: [block.node], section: $pos.node(1) }
}

type DocMeta = Omit<EditorDocument, 'pmDoc'>

export function blockContentDocument(range: Pick<BlockRange, 'nodes' | 'section'>, meta: DocMeta): CanonicalDocument {
  const attrs = { ...range.section.attrs }
  for (const k of ['header', 'footer', 'firstPageHeader', 'firstPageFooter', 'evenPageHeader', 'evenPageFooter']) attrs[k] = null
  const pmDoc: JSONContent = { type: 'doc', content: [{ type: 'section', attrs, content: range.nodes.map(n => n.toJSON()) }] }
  const doc = docToCdm({ ...meta, pmDoc })
  const refs = { footnoteId: new Set<number>(), endnoteId: new Set<number>(), commentId: new Set<number>() }
  collectRefs(doc.sections, refs)
  return stripObjectUrls({
    ...doc,
    footnotes: meta.footnotes.filter(n => refs.footnoteId.has(n.id)),
    endnotes: meta.endnotes.filter(n => refs.endnoteId.has(n.id)),
    comments: meta.comments.filter(c => refs.commentId.has(c.id)),
  })
}

export const blockContentJson = (range: Pick<BlockRange, 'nodes' | 'section'>, meta: DocMeta) =>
  JSON.stringify(blockContentDocument(range, meta))

function collectRefs(value: unknown, refs: Record<'footnoteId' | 'endnoteId' | 'commentId', Set<number>>) {
  if (Array.isArray(value)) { value.forEach(v => collectRefs(v, refs)); return }
  if (!value || typeof value !== 'object') return
  for (const [k, v] of Object.entries(value)) {
    if ((k === 'footnoteId' || k === 'endnoteId' || k === 'commentId') && typeof v === 'number') refs[k].add(v)
    else collectRefs(v, refs)
  }
}

// An editor image's resolvedSource is a blob: URL that only means something in this tab.
function stripObjectUrls<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripObjectUrls) as T
  if (!value || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    out[k] = k === 'resolvedSource' && typeof v === 'string' && v.startsWith('blob:') ? null : stripObjectUrls(v)
  }
  return out as T
}

/** Search over name, description, category and tags. */
export function matchesBlock(b: ContentBlockSummary, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [b.name, b.description ?? '', b.category ?? '', ...(b.tags ?? [])].some(s => s.toLowerCase().includes(q))
}

/** A block's contentJson as top-level editor nodes (its first section's body). */
export function blockContentNodes(contentJson: string): JSONContent[] {
  const doc = JSON.parse(contentJson) as Partial<CanonicalDocument>
  return blocksToPm(doc.sections?.[0]?.body ?? [])
}

export interface DocNotes {
  footnotes: Footnote[]
  endnotes: Endnote[]
  comments: CdmComment[]
}

/** Where an inserted block's notes go (the page's document meta). After `add`, `current` must include them. */
export interface NoteSink {
  current: () => DocNotes
  add: (notes: DocNotes) => void
}

type RefKey = 'footnoteId' | 'endnoteId' | 'commentId'
type IdMaps = Record<RefKey, Map<number, number>>

const nextId = (items: { id: number }[]) => items.reduce((m, n) => Math.max(m, n.id), 0) + 1

/**
 * Renumbers a block's footnotes/endnotes/comments past the ids `existing` uses and rewrites every
 * reference to them, in its sections and inside the notes themselves. A reference the block has no
 * note for is renumbered too, so it cannot land on an unrelated note of the open document.
 */
export function remapBlockNotes(block: Partial<CanonicalDocument>, existing: DocNotes): { sections: DocSection[]; notes: DocNotes } {
  const sections = block.sections ?? []
  const notes: DocNotes = { footnotes: block.footnotes ?? [], endnotes: block.endnotes ?? [], comments: block.comments ?? [] }
  const used: Record<RefKey, Set<number>> = { footnoteId: new Set(), endnoteId: new Set(), commentId: new Set() }
  collectRefs([sections, notes], used)
  notes.footnotes.forEach(n => used.footnoteId.add(n.id))
  notes.endnotes.forEach(n => used.endnoteId.add(n.id))
  notes.comments.forEach(c => used.commentId.add(c.id))

  const start: Record<RefKey, number> = {
    footnoteId: nextId(existing.footnotes), endnoteId: nextId(existing.endnotes), commentId: nextId(existing.comments),
  }
  const maps = {} as IdMaps
  for (const k of Object.keys(used) as RefKey[]) {
    maps[k] = new Map([...used[k]].sort((a, b) => a - b).map((id, i) => [id, start[k] + i]))
  }
  const rewrite = <T>(v: T) => rewriteRefs(v, maps)
  return {
    sections: rewrite(sections),
    notes: {
      footnotes: notes.footnotes.map(n => ({ ...rewrite(n), id: maps.footnoteId.get(n.id)! })),
      endnotes: notes.endnotes.map(n => ({ ...rewrite(n), id: maps.endnoteId.get(n.id)! })),
      comments: notes.comments.map(c => ({ ...rewrite(c), id: maps.commentId.get(c.id)! })),
    },
  }
}

function rewriteRefs<T>(value: T, maps: IdMaps): T {
  if (Array.isArray(value)) return value.map(v => rewriteRefs(v, maps)) as T
  if (!value || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    const map = (maps as Record<string, Map<number, number> | undefined>)[k]
    out[k] = map && typeof v === 'number' ? map.get(v) ?? v : rewriteRefs(v, maps)
  }
  return out as T
}

/** The ids of the notes/comments inserted team blocks brought into the document. */
export interface InsertedNoteIds {
  footnotes: Set<number>
  endnotes: Set<number>
  comments: Set<number>
}

export const emptyInsertedNoteIds = (): InsertedNoteIds => ({ footnotes: new Set(), endnotes: new Set(), comments: new Set() })

export function recordInsertedNotes(ids: InsertedNoteIds, notes: DocNotes) {
  notes.footnotes.forEach(n => ids.footnotes.add(n.id))
  notes.endnotes.forEach(n => ids.endnotes.add(n.id))
  notes.comments.forEach(c => ids.comments.add(c.id))
}

/** Drops inserted notes/comments nothing references any more (the insert was undone, or the text deleted). */
export function pruneInsertedNotes(doc: CanonicalDocument, inserted: InsertedNoteIds): CanonicalDocument {
  if (!inserted.footnotes.size && !inserted.endnotes.size && !inserted.comments.size) return doc
  const refs = { footnoteId: new Set<number>(), endnoteId: new Set<number>(), commentId: new Set<number>() }
  collectRefs(doc.sections, refs)
  // A reference inside a note only counts while that note is itself still referenced.
  collectRefs(doc.footnotes.filter(n => !inserted.footnotes.has(n.id) || refs.footnoteId.has(n.id)), refs)
  collectRefs(doc.endnotes.filter(n => !inserted.endnotes.has(n.id) || refs.endnoteId.has(n.id)), refs)
  const keep = (ids: Set<number>, used: Set<number>) => (n: { id: number }) => !ids.has(n.id) || used.has(n.id)
  return {
    ...doc,
    footnotes: doc.footnotes.filter(keep(inserted.footnotes, refs.footnoteId)),
    endnotes: doc.endnotes.filter(keep(inserted.endnotes, refs.endnoteId)),
    comments: doc.comments.filter(keep(inserted.comments, refs.commentId)),
  }
}

export interface InsertTeamBlockOptions {
  /** The version open in the editor; null/absent on a never-saved template, whose images are then read from its staged assets. */
  versionId?: string | null
  /** Receives the block's footnotes/endnotes/comments. Without one they are not merged. */
  notes?: NoteSink
}

/**
 * Inserts a copy of a team block: asks the server for a copy (staging its images for this template),
 * then inserts it as one history step at the cursor or `pos`, mapped through any edits made while the
 * request ran. Images are fetched through the template asset endpoint — by version (which falls back
 * to staged images), or by template id before the first save — so they show at once.
 */
export async function insertTeamBlock(editor: Editor, templateId: string, contentBlockId: string, pos?: number,
  options: InsertTeamBlockOptions = {}): Promise<boolean> {
  let at = pos
  const onTx = ({ transaction }: { transaction: { mapping: { map: (p: number) => number } } }) => {
    if (at !== undefined) at = transaction.mapping.map(at)
  }
  editor.on('transaction', onTx)
  try {
    const { contentJson } = await blocksApiService.insert(contentBlockId, templateId)
    if (editor.isDestroyed) return false
    const block = JSON.parse(contentJson) as Partial<CanonicalDocument>
    const versionId = options.versionId
    const urls = await fetchImageUrls(blocksToPm(block.sections?.[0]?.body ?? []), ref =>
      versionId ? templatesApiService.getAsset(versionId, ref) : templatesApiService.getStagedAsset(templateId, ref))
    if (editor.isDestroyed) return false
    // Ids are taken after the awaits, so two inserts in flight cannot be given the same ones.
    const remapped = options.notes ? remapBlockNotes(block, options.notes.current()) : null
    const body = (remapped ? remapped.sections : block.sections ?? [])[0]?.body ?? []
    const nodes = blocksToPm(body).map(n => resolveImageSources(n, urls))
    if (nodes.length === 0 || !insertBlocks(editor, nodes, at)) return false
    const notes = remapped?.notes
    if (notes && (notes.footnotes.length || notes.endnotes.length || notes.comments.length)) options.notes!.add(notes)
    return true
  } finally {
    editor.off('transaction', onTx)
  }
}

const saveRequests = new WeakMap<Editor, (range: BlockRange) => void>()

/** Registers what "Save as block…" does — the editor page's save modal. */
export function setSaveBlockHandler(editor: Editor, handler: ((range: BlockRange) => void) | null) {
  if (handler) saveRequests.set(editor, handler)
  else saveRequests.delete(editor)
}

export const hasSaveBlockHandler = (editor: Editor) => saveRequests.has(editor)

export function requestSaveBlock(editor: Editor, range: BlockRange): boolean {
  const handler = saveRequests.get(editor)
  if (!handler) return false
  handler(range)
  return true
}

async function fetchImageUrls(nodes: JSONContent[], fetch: (blobRef: string) => Promise<Blob>): Promise<Map<string, string>> {
  const refs = new Set<string>()
  nodes.forEach(n => collectImageBlobRefs(n, refs))
  const urls = new Map<string, string>()
  await Promise.all([...refs].map(async ref => {
    try {
      urls.set(ref, URL.createObjectURL(await fetch(ref)))
    } catch {
      // Not reachable yet: the image keeps its blobRef and shows a placeholder until the next load.
    }
  }))
  return urls
}
